import { DataTable } from "../DataTable";
import React, { useEffect, useMemo, useState } from "react";
import {
  calculateDataTableState,
  constructQueryParamsString,
  formatDate,
  handlePageSizeChange,
  showErrorToast
} from "../../utils";
import { useToast } from "@chakra-ui/react";
import { notificationKeys, useGetNotifications, useUpdateNotificationStatus } from "../../hooks/query";
import { Page } from "../Page";
import { Card } from "../Card";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

export const AllNotification = () => {
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation("translation", {
    keyPrefix: "actionNotifications"
  });

  const [notificationList, setNotificationList] = useState([]);
  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });


  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const getQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    return constructQueryParamsString(queryParams);
  };

  const {
    isFetching: isFetchingNotifications
  } = useGetNotifications(
    getQueryParamsString(),
    {
      onSuccess: (data) => {
        setNotificationList(data?.content);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      cacheTime: 0
    });

  const getRequestConfig = () => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        queryClient.invalidateQueries(notificationKeys.all());
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner())
    };
  };

  const { mutate: updateNotificationStatus } = useUpdateNotificationStatus(getRequestConfig());

  useEffect(() => {
    // get list of id of notifications
    const notificationIds = notificationList.map(notification => notification.id);
    if (notificationIds.length > 0) {
      updateNotificationStatus(notificationIds);
    }
  }, [notificationList, updateNotificationStatus]);

  const columns = useMemo(() => {
      return [
        {
          header: t("table.message"),
          accessorKey: "message",
          size: 150,
          enableColumnFilter: false
        },
        {
          header: t("table.action"),
          accessorKey: "action",
          size: 50,
          cell: ({ cell }) => cell.getValue() ? t(`actions.${cell.getValue()}`) : ""
        },
        {
          header: t("table.date"),
          accessorKey: "createdDate",
          size: 50,
          cell: ({ cell }) => formatDate(new Date(cell.getValue()))

        }
      ];
    }, [t]
  );

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject
        })
      };
    });
  };


  return (
    <Page>
      <Card>
        <DataTable
          data={notificationList}
          columns={columns}
          isLoading={isFetchingNotifications}
          serverSidePagination={true}
          queryTotalCount={totalElements}
          queryPageCount={totalPages}
          queryPageIndex={queryPageIndex}
          setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
          queryPageSize={queryPageSize}
          setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
          queryPageSortBy={queryPageSortBy}
          setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
          filterObj={filterObj}
          setFilterObj={(newFilterObj) => manageDataTableState({
            filterObj: newFilterObj,
            queryPageIndex: 0
          })}
          enableFilters={false}
          enableSorting={false}
          containerHeight="var(--chakra-sizes-2xl)"
        />
      </Card>
    </Page>
  );
};
